import * as React from 'react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardDataTableSortDirection,
  DashboardTableSearchInput,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import { AutocompleteFilter } from '@shigo-ui/ui';

import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { canEditDomainCompliance } from '../../domainCompliancePermissions';
import { useDomainPublishersQuery, useDomainsQuery } from '../../hooks';
import {
  domainsPageSizes,
  DomainsSortDirection,
  DomainsSortProperty,
  isDomainsSortProperty,
  type DomainComplianceSummary,
} from '../../model';
import { useDomainsRouteFilters } from '../../useDomainsRouteFilters';
import { DomainComplianceDialog } from '../DomainComplianceDialog/DomainComplianceDialog';
import { PublisherDomainsTable } from '../PublisherDomainsTable';
import { ComplianceSummary } from './ComplianceSummary';

export function DomainsTable({ publisherId }: { publisherId?: string } = {}) {
  return publisherId ? (
    <PublisherDomainsTable publisherId={publisherId} />
  ) : (
    <DomainComplianceTable />
  );
}

function DomainComplianceTable() {
  const { can } = useShigoPermissions();
  const routeFilters = useDomainsRouteFilters(false);
  const { filter } = routeFilters;
  const query = useDomainsQuery(filter);
  const publishersQuery = useDomainPublishersQuery();
  const search = useDebouncedValue(filter.searchTerm, routeFilters.setSearch, 650);
  const [selectedDomain, setSelectedDomain] = React.useState<DomainComplianceSummary | null>(null);
  const canReadApprovals = can(ShigoPermission.DemandComplianceDpsRead);
  const canEditApprovals = canReadApprovals && canEditDomainCompliance(can);
  const rows = query.data?.content ?? [];
  const columns = buildDashboardDataTableColumns<DomainComplianceSummary>([
    {
      id: DomainsSortProperty.Publisher,
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('supplyDomains.publisher'),
      headerClassName: 'w-[34%]',
      className: 'w-[34%] font-medium',
      sortable: true,
      value: (domain) => domain.publisherName,
    },
    {
      id: 'name',
      sortKey: DomainsSortProperty.Url,
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('supplyDomains.domainName'),
      headerClassName: 'w-[38%]',
      className: 'w-[38%]',
      sortable: true,
      value: (domain) => domain.url,
    },
    {
      id: 'compliance',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('supplyDomains.compliance'),
      headerClassName: 'w-[28%]',
      className: 'w-[28%]',
      cell: (domain) => (
        <ComplianceSummary
          domain={domain}
          interactive={canEditApprovals}
          visible={canReadApprovals}
          onOpen={setSelectedDomain}
        />
      ),
    },
  ]);

  return (
    <>
      <DashboardDataTable
        title={dashboardT('supplyDomains.title')}
        tableLabel={dashboardT('supplyDomains.title')}
        columns={columns}
        items={rows}
        getRowKey={(domain) => `${domain.publisherName}:${domain.url}`}
        loading={query.isLoading}
        loadingMessage={dashboardT('supplyDomains.loading')}
        errorMessage={query.isError ? dashboardT('supplyDomains.loadError') : undefined}
        emptyMessage={dashboardT('supplyDomains.empty')}
        toolbar={
          <>
            <DashboardTableSearchInput
              aria-label={dashboardT('supplyDomains.search')}
              placeholder={dashboardT('supplyDomains.search')}
              value={search.draft}
              onChange={(event) => {
                search.setDraft(event.target.value);
              }}
            />
            <AutocompleteFilter
              label={dashboardT('supplyDomains.publisherFilter')}
              options={(publishersQuery.data ?? []).map((publisher) => ({
                id: publisher.id,
                label: publisher.name,
              }))}
              selectedIds={filter.publisherIds}
              onSelectedIdsChange={routeFilters.setPublishers}
              loading={publishersQuery.isLoading}
              searchPlaceholder={dashboardT('supplyDomains.publisherSearch')}
              className="w-[280px] max-w-full"
            />
          </>
        }
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: domainsPageSizes,
          disabled: query.isFetching,
          onChange: (next) => {
            routeFilters.setPagination(next.page, next.pageSize);
          },
        }}
        sort={{
          value: filter.sortProperty
            ? {
                key: filter.sortProperty,
                direction:
                  filter.sortDirection === DomainsSortDirection.Ascending
                    ? DashboardDataTableSortDirection.Asc
                    : DashboardDataTableSortDirection.Desc,
              }
            : undefined,
          onChange: (next) => {
            if (!next) {
              routeFilters.setSort(null, filter.sortDirection);
              return;
            }
            if (!isDomainsSortProperty(next.key)) return;
            routeFilters.setSort(
              next.key,
              next.direction === DashboardDataTableSortDirection.Asc
                ? DomainsSortDirection.Ascending
                : DomainsSortDirection.Descending,
            );
          },
        }}
      />
      <DomainComplianceDialog
        domain={selectedDomain}
        onOpenChange={(open) => {
          if (!open) setSelectedDomain(null);
        }}
      />
    </>
  );
}
